import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Check } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Quotation = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    company_name: '',
    gst_number: '',
    contact_person: '',
    designation: '',
    mobile: '',
    email: '',
    project_name: '',
    project_location: '',
    industry_type: '',
    nature_of_work: [],
    estimated_quantity: '',
    start_date: '',
    completion_deadline: '',
    scope_description: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const steps = [
    { number: 1, title: 'Client Information' },
    { number: 2, title: 'Project Details' },
    { number: 3, title: 'Technical Specifications' },
    { number: 4, title: 'Review & Submit' },
  ];

  const workOptions = [
    'Structural Steel Fabrication',
    'Industrial Erection',
    'Furnace Installation',
    'Crane Bay Structures',
    'Pollution Control Systems',
    'DRI Projects',
    'Maintenance & Shutdown',
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (work) => {
    const updated = formData.nature_of_work.includes(work)
      ? formData.nature_of_work.filter((w) => w !== work)
      : [...formData.nature_of_work, work];
    setFormData({ ...formData, nature_of_work: updated });
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await axios.post(`${API}/quotations`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting quotation:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, 4));
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 py-12 px-4" data-testid="success-message">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white p-12 shadow-xl border-l-4 border-green-500 max-w-2xl text-center"
        >
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-12 h-12 text-white" />
          </div>
          <h2 className="font-oswald font-bold text-3xl text-slate-900 mb-4">
            QUOTATION REQUEST SUBMITTED
          </h2>
          <p className="text-slate-700 mb-6">
            Thank you for your interest. Our engineering team will review your requirements and 
            respond with a detailed techno-commercial quotation shortly.
          </p>
          <p className="text-sm text-slate-600 mb-8">
            You will receive a confirmation email at <strong>{formData.email}</strong>
          </p>
          <button
            onClick={() => navigate('/')}
            data-testid="back-home-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold font-oswald px-8 py-3 transition-all"
          >
            BACK TO HOME
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12" data-testid="quotation-header">
          <h1 className="font-oswald font-bold text-4xl md:text-5xl text-slate-900 mb-4">
            REQUEST FOR QUOTATION
          </h1>
          <p className="text-slate-600">
            Complete the form below to receive a detailed engineering quotation
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-12" data-testid="progress-bar">
          <div className="flex justify-between items-center">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-oswald font-bold ${
                      currentStep >= step.number
                        ? 'bg-orange-500 text-white'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {step.number}
                  </div>
                  <p className="text-xs mt-2 text-slate-600 hidden md:block">{step.title}</p>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      currentStep > step.number ? 'bg-orange-500' : 'bg-slate-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white shadow-lg border-l-4 border-orange-500 p-8">
          <AnimatePresence mode="wait">
            {/* Step 1 */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                data-testid="step-1"
              >
                <h2 className="font-oswald font-bold text-2xl text-slate-900 mb-6">CLIENT INFORMATION</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Company Name *</label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      data-testid="company-name-input"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">GST Number</label>
                    <input
                      type="text"
                      name="gst_number"
                      value={formData.gst_number}
                      onChange={handleInputChange}
                      data-testid="gst-input"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Contact Person *</label>
                    <input
                      type="text"
                      name="contact_person"
                      value={formData.contact_person}
                      onChange={handleInputChange}
                      data-testid="contact-person-input"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Designation *</label>
                    <input
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                      data-testid="designation-input"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Mobile *</label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      data-testid="mobile-input"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      data-testid="email-input"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      required
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2 */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                data-testid="step-2"
              >
                <h2 className="font-oswald font-bold text-2xl text-slate-900 mb-6">PROJECT DETAILS</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Project Name *</label>
                    <input
                      type="text"
                      name="project_name"
                      value={formData.project_name}
                      onChange={handleInputChange}
                      data-testid="project-name-input"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Project Location *</label>
                    <input
                      type="text"
                      name="project_location"
                      value={formData.project_location}
                      onChange={handleInputChange}
                      data-testid="project-location-input"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Industry Type *</label>
                    <select
                      name="industry_type"
                      value={formData.industry_type}
                      onChange={handleInputChange}
                      data-testid="industry-type-select"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      required
                    >
                      <option value="">Select Industry Type</option>
                      <option value="Steel Plant">Steel Plant</option>
                      <option value="Ferro Alloy Unit">Ferro Alloy Unit</option>
                      <option value="Sponge Iron Plant">Sponge Iron Plant</option>
                      <option value="Rolling Mill">Rolling Mill</option>
                      <option value="Foundry">Foundry</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3 */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                data-testid="step-3"
              >
                <h2 className="font-oswald font-bold text-2xl text-slate-900 mb-6">TECHNICAL SPECIFICATIONS</h2>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-3">Nature of Work * (Select all that apply)</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {workOptions.map((work) => (
                        <label
                          key={work}
                          className="flex items-center space-x-2 p-3 bg-slate-50 border border-slate-200 hover:border-orange-500 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.nature_of_work.includes(work)}
                            onChange={() => handleCheckboxChange(work)}
                            data-testid={`work-${work.toLowerCase().replace(/\s+/g, '-')}`}
                            className="text-orange-500 focus:ring-orange-500"
                          />
                          <span className="text-sm text-slate-700">{work}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Estimated Quantity (MT) *</label>
                      <input
                        type="text"
                        name="estimated_quantity"
                        value={formData.estimated_quantity}
                        onChange={handleInputChange}
                        data-testid="quantity-input"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Start Date</label>
                      <input
                        type="date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleInputChange}
                        data-testid="start-date-input"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Completion Deadline</label>
                      <input
                        type="date"
                        name="completion_deadline"
                        value={formData.completion_deadline}
                        onChange={handleInputChange}
                        data-testid="deadline-input"
                        className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 h-12 px-4"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Scope Description *</label>
                    <textarea
                      name="scope_description"
                      value={formData.scope_description}
                      onChange={handleInputChange}
                      data-testid="scope-textarea"
                      rows="6"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 px-4 py-3"
                      placeholder="Provide detailed description of work scope, specifications, and any special requirements..."
                      required
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4 */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                data-testid="step-4"
              >
                <h2 className="font-oswald font-bold text-2xl text-slate-900 mb-6">REVIEW & SUBMIT</h2>
                <div className="space-y-6">
                  <div className="bg-slate-50 p-6 border-l-4 border-orange-500">
                    <h3 className="font-oswald font-bold text-lg text-slate-900 mb-4">CLIENT INFORMATION</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><strong>Company:</strong> {formData.company_name}</div>
                      <div><strong>Contact:</strong> {formData.contact_person}</div>
                      <div><strong>Mobile:</strong> {formData.mobile}</div>
                      <div><strong>Email:</strong> {formData.email}</div>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-6 border-l-4 border-orange-500">
                    <h3 className="font-oswald font-bold text-lg text-slate-900 mb-4">PROJECT DETAILS</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><strong>Project:</strong> {formData.project_name}</div>
                      <div><strong>Location:</strong> {formData.project_location}</div>
                      <div><strong>Industry:</strong> {formData.industry_type}</div>
                      <div><strong>Quantity:</strong> {formData.estimated_quantity} MT</div>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-6 border-l-4 border-orange-500">
                    <h3 className="font-oswald font-bold text-lg text-slate-900 mb-4">NATURE OF WORK</h3>
                    <div className="flex flex-wrap gap-2">
                      {formData.nature_of_work.map((work) => (
                        <span key={work} className="bg-orange-500 text-white px-3 py-1 text-sm">
                          {work}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-orange-50 p-6 border-l-4 border-orange-500">
                    <p className="text-sm text-slate-700">
                      By submitting this form, you agree that our engineering team will contact you with a 
                      detailed techno-commercial quotation based on your requirements.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-8 border-t border-slate-200">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              data-testid="prev-btn"
              className={`flex items-center space-x-2 font-oswald px-6 py-3 transition-all ${
                currentStep === 1
                  ? 'text-slate-400 cursor-not-allowed'
                  : 'text-slate-700 hover:text-orange-500'
              }`}
            >
              <ChevronLeft size={20} />
              <span>PREVIOUS</span>
            </button>

            {currentStep < 4 ? (
              <button
                onClick={nextStep}
                data-testid="next-btn"
                className="flex items-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white font-oswald px-8 py-3 transition-all"
              >
                <span>NEXT</span>
                <ChevronRight size={20} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="submit-btn"
                className="bg-orange-500 hover:bg-orange-600 text-white font-oswald px-10 py-3 transition-all disabled:opacity-50"
              >
                {submitting ? 'SUBMITTING...' : 'SUBMIT FOR ENGINEERING REVIEW'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quotation;